

// export default CreateEmployee;

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles } from "../redux/slices/roleSlice";
import { getBranches } from "../redux/slices/branchSlice";
import { submitEmployee, resetEmployeeStatus } from "../redux/slices/employeeSlice";
import "../assets/CreateEmployee.css";

const CreateEmployee = () => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [postOfficeList, setPostOfficeList] = useState([]);
  const firstErrorRef = useRef(null);
  const dispatch = useDispatch();

  const { items: roles } = useSelector((state) => state.roles);
  const { branches } = useSelector((state) => state.branch);
  const { loading, error, success } = useSelector((state) => state.employee);

  useEffect(() => {
    dispatch(fetchRoles());
    // dispatch(getBranches());
    dispatch(getBranches({ fetchAll: true }));

  }, [dispatch]);

  // ✅ Validation rules
  const validators = {
    panNumber: (v) =>
      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v) || "Invalid PAN (ABCDE1234F)",
    aadharNumber: (v) =>
      /^[0-9]{12}$/.test(v) || "Aadhaar must be 12 digits",
    mobile: (v) =>
      /^[0-9]{10}$/.test(v) || "Mobile must be 10 digits",
    pincode: (v) =>
      /^[0-9]{6}$/.test(v) || "Pincode must be 6 digits",
    bankAccount: (v) =>
      /^[0-9]{9,18}$/.test(v) || "Bank Account must be 9–18 digits",
    ifsc: (v) =>
      /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v) || "Invalid IFSC (e.g. SBIN0XXXXXX)",
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // uppercase for PAN & IFSC
    const newValue = ["panNumber", "ifsc"].includes(name)
      ? value.toUpperCase()
      : value;

    if (name === "pincode" && newValue.length > 6) return;

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // clear error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    // fetch Post Office by Pincode
    if (name === "pincode" && /^\d{6}$/.test(newValue)) {
      try {
        const res = await fetch(
          `https://payyug.in/api/customer/pincode/${newValue}`
        );

        const data = await res.json();

        console.log("PINCODE API RESPONSE:", data);

        if (data.success && data.PostOffice?.length > 0) {

          setPostOfficeList(data.PostOffice);

          const po = data.PostOffice[0];

          setFormData((prev) => ({
            ...prev,
            postOffice: po.Name || "",
            subDistrict: po.Block || "",
            district: po.District || "",
            state: po.State || "",
            country: po.Country || "India",
          }));

        } else {
          setPostOfficeList([]);
          alert("No post offices found for this PIN");
        }

      } catch (err) {
        console.error("Error fetching pincode:", err);
      }
    }

    // fetch Bank Details by IFSC
    if (name === "ifsc" && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(newValue)) {
      try {
        const res = await fetch(`https://ifsc.razorpay.com/${newValue}`);
        if (res.ok) {
          const data = await res.json();
          setFormData((prev) => ({
            ...prev,
            bankName: data.BANK,
            bankBranch: data.BRANCH,
          }));
        } else {
          setFormData((prev) => ({ ...prev, bankName: "", bankBranch: "" }));
        }
      } catch (err) {
        console.error("Error fetching IFSC:", err);
      }
    }
  };

  const handlePostOfficeSelect = (e) => {
    const poName = e.target.value;

    const po = postOfficeList.find(
      (p) => p.Name === poName
    );

    if (po) {
      setFormData((prev) => ({
        ...prev,
        postOffice: po.Name || "",
        subDistrict: po.Block || "",
        district: po.District || "",
        state: po.State || "",
        country: po.Country || "India",
      }));
    }
  };

  const scrollToError = () => {
    firstErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ✅ Updated validation function
  const validateForm = () => {
    const newErr = {};

    Object.entries(validators).forEach(([field, rule]) => {
      if (formData[field] && rule(formData[field]) !== true) {
        newErr[field] = rule(formData[field]);
      }
    });

    // required fields
    ["fullName", "guardianName", "mobile", "email", "pincode"].forEach((f) => {
      if (!formData[f]) newErr[f] = "Required field.";
    });
    ["district", "state", "country", "dob", "doj"].forEach((f) => {
      if (!formData[f]) newErr[f] = "Required field.";
    });

    setErrors(newErr);

    if (Object.keys(newErr).length) {
      scrollToError();
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const token = localStorage.getItem("authToken");
    if (!token) {
      alert("No auth token found in localStorage.");
      return;
    }

    const branchObj = branches.find((b) => b.name === formData.branch);
    const roleObj = roles.find((r) => r.name === formData.role);

    const payload = {
      branchId: branchObj?.id || 1,
      branchName: branchObj?.name || "Unknown",
      superAdminId: localStorage.getItem('adminuserId'),
      password: "1234",
      pin: "1234",
      role: roleObj?.name || "user",
      roleName: roleObj?.name || "user",
      panNumber: formData.panNumber,
      aadhaarNumber: formData.aadharNumber,
      gender: formData.gender,
      fullName: formData.fullName,
      guardianName: formData.guardianName,
      mobileNumber: formData.mobile,
      email: formData.email,
      pincode: formData.pincode,
      postOffice: formData.postOffice,
      bankAccount: formData.bankAccount,
      bankIFSC: formData.ifsc,
      bankBranch: formData.bankBranch,
      bankName: formData.bankName,
      subDistrict: formData.subDistrict,
      district: formData.district,
      state: formData.state,
      country: formData.country,
      address: formData.address,
      maritalStatus: formData.maritalStatus,
      dateOfBirth: formData.dob,
      dateOfJoining: formData.doj,
    };

    console.log("Submitting employee:", payload);
    dispatch(submitEmployee({ data: payload, token }));
    setFormData({});
  };

  useEffect(() => {
    if (success) {
      alert("Employee submitted successfully!");
      dispatch(resetEmployeeStatus());
    }
    if (error) {
      alert("Error: " + error);
      dispatch(resetEmployeeStatus());
    }
  }, [success, error, dispatch]);

  const classErr = (f) => (errors[f] ? "CreateEmployee-input-error" : "");
  const renderError = (f) =>
    errors[f] ? (
      <p
        className="CreateEmployee-error-text"
        ref={!firstErrorRef.current ? firstErrorRef : null}
      >
        {errors[f]}
      </p>
    ) : null;

return (
   <div className="CreateEmployee-container">
      <form className="CreateEmployee-form" onSubmit={(e) => e.preventDefault()}>
        <h2 className="CreateEmployee-title">Create New Employee</h2>

        {/* Personal Info */}
        <section className="CreateEmployee-section">
          <h3 className="CreateEmployee-section-title">Personal Information</h3>
          <div className="CreateEmployee-grid">
            <div>
              <label>PAN Number <span style={{ color: "red" }}>*</span></label>
              <input name="panNumber" maxLength="10" className={classErr("panNumber")}
                value={formData.panNumber || ""} onChange={handleChange} required />
              {renderError("panNumber")}
            </div>

            <div>
              <label>Aadhaar Number <span style={{ color: "red" }}>*</span></label>
              <input name="aadharNumber" maxLength="12" className={classErr("aadharNumber")}
                value={formData.aadharNumber || ""} onChange={handleChange} required />
              {renderError("aadharNumber")}
            </div>

            <div>
              <label>Branch <span style={{ color: "red" }}>*</span></label>
              <select name="branch" value={formData.branch || ""} onChange={handleChange} required>
                <option value="">Select Branch</option>
                {(branches || []).map((b) => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
              </select>
              {renderError("branch")}
            </div>

            <div>
              <label>Role <span style={{ color: "red" }}>*</span></label>
              <select name="role" value={formData.role || ""} onChange={handleChange} required>
                <option value="">Select Role</option>
                {(roles || []).map((r) => (
                  <option key={r.id} value={r.name}>{r.name}</option>
                ))}
              </select>
              {renderError("role")}
            </div>

            <div>
              <label>Title</label>
              <select name="title" value={formData.title || ""} onChange={handleChange}>
                <option>Select</option>
                <option>Mr.</option>
                <option>Mrs.</option>
                <option>Miss</option>
                <option>Dr.</option>
              </select>
            </div>

            <div>
              <label>Gender <span style={{ color: "red" }}>*</span></label>
              <select name="gender" value={formData.gender || ""} onChange={handleChange} required>
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
              {renderError("gender")}
            </div>
          </div>
        </section>

        {/* Contact & Banking */}
        <section className="CreateEmployee-section">
          <h3 className="CreateEmployee-section-title">Contact & Banking</h3>
          <div className="CreateEmployee-grid">
            <div>
              <label>Full Name <span style={{ color: "red" }}>*</span></label>
              <input name="fullName" className={classErr("fullName")}
                value={formData.fullName || ""} onChange={handleChange} required />
              {renderError("fullName")}
            </div>

            <div>
              <label>Parents Name <span style={{ color: "red" }}>*</span></label>
              <input name="guardianName" className={classErr("guardianName")}
                value={formData.guardianName || ""} onChange={handleChange} required />
              {renderError("guardianName")}
            </div>

            <div>
              <label>Mobile <span style={{ color: "red" }}>*</span></label>
              <input name="mobile" maxLength="10" className={classErr("mobile")}
                value={formData.mobile || ""} onChange={handleChange} required />
              {renderError("mobile")}
            </div>

            <div>
              <label>Email <span style={{ color: "red" }}>*</span></label>
              <input name="email" className={classErr("email")}
                value={formData.email || ""} onChange={handleChange} required />
              {renderError("email")}
            </div>

            <div>
              <label>Pincode <span style={{ color: "red" }}>*</span></label>
              <input name="pincode" maxLength="6" className={classErr("pincode")}
                value={formData.pincode || ""} onChange={handleChange} required />
              {renderError("pincode")}
            </div>

            <div>
              <label>Post Office</label>
              <select name="postOffice" value={formData.postOffice || ""} onChange={handlePostOfficeSelect}>
                <option>Select Post Office</option>
                {postOfficeList.map((p, index) => (
                  <option key={index} value={p.Name}>{p.Name}</option>
                ))}
              </select>
            </div>

            <div>
              <label>Bank Account <span style={{ color: "red" }}>*</span></label>
              <input name="bankAccount" className={classErr("bankAccount")}
                value={formData.bankAccount || ""} onChange={handleChange} required />
              {renderError("bankAccount")}
            </div>

            <div>
              <label>IFSC <span style={{ color: "red" }}>*</span></label>
              <input name="ifsc" maxLength="11" className={classErr("ifsc")}
                value={formData.ifsc || ""} onChange={handleChange} required />
              {renderError("ifsc")}
            </div>

            <div>
              <label>Bank Branch<span style={{ color: "red" }}>*</span></label>
              <input name="bankBranch" value={formData.bankBranch || ""} readOnly />
            </div>

            <div>
              <label>Bank Name<span style={{ color: "red" }}>*</span></label>
              <input name="bankName" value={formData.bankName || ""} readOnly />
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="CreateEmployee-section">
          <h3 className="CreateEmployee-section-title">Address</h3>
          <div className="CreateEmployee-grid">
            <div>
              <label>Sub District</label>
              <input name="subDistrict" value={formData.subDistrict || ""} readOnly />
            </div>

            <div>
              <label>District <span style={{ color: "red" }}>*</span></label>
              <input name="district" className={classErr("district")}
                value={formData.district || ""} readOnly required />
              {renderError("district")}
            </div>

            <div>
              <label>State <span style={{ color: "red" }}>*</span></label>
              <input name="state" className={classErr("state")}
                value={formData.state || ""} readOnly required />
              {renderError("state")}
            </div>

            <div>
              <label>Country <span style={{ color: "red" }}>*</span></label>
              <input name="country" className={classErr("country")}
                value={formData.country || ""} readOnly required />
              {renderError("country")}
            </div>

            <div>
              <label>Address<span style={{ color: "red" }}>*</span></label>
              <input name="address" value={formData.address || ""} onChange={handleChange} />
            </div>

            <div>
              <label>Marital Status<span style={{ color: "red" }}>*</span></label>
              <select name="maritalStatus" value={formData.maritalStatus || ""} onChange={handleChange}>
                <option>Select</option>
                <option>Single</option>
                <option>Married</option>
                <option>Other</option>
              </select>
            </div>

            <div>
              <label>Date of Birth <span style={{ color: "red" }}>*</span></label>
              <input type="date" name="dob" className={classErr("dob")}
                value={formData.dob || ""} onChange={handleChange} required />
              {renderError("dob")}
            </div>

            <div>
              <label>Date of Joining <span style={{ color: "red" }}>*</span></label>
              <input type="date" name="doj" className={classErr("doj")}
                value={formData.doj || ""} onChange={handleChange} required />
              {renderError("doj")}
            </div>
          </div>
        </section>

        <div className="CreateEmployee-button-group">
          <button type="button" className="CreateEmployee-btn"
            onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEmployee;